import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  User,
  Building2,
  Shield,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Briefcase,
  FileText,
  Edit2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/utils/trpc";

interface ViewUserDialogProps {
  user: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
}

function FieldDisplay({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">{label}</p>
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-sm font-medium text-[#2C2C2C]">{value || "—"}</p>
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="border-l-4 border-[#5B2C93] pl-3 mb-4">
      <h3 className="text-sm font-medium text-[#5B2C93]">{title}</h3>
    </div>
  );
}

export default function ViewUserDialog({ user, open, onOpenChange, onEdit }: ViewUserDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");

  const { data: departmentsData } = trpc.departments.list.useQuery({});
  const { data: companiesData } = trpc.masterData.getAllCompanies.useQuery({});
  const { data: sitesData } = trpc.sites.getAll.useQuery();

  const departments = departmentsData || [];
  const companies = companiesData || [];
  const sites = sitesData || [];

  if (!user) return null;

  const getUserInitials = () => {
    const first = user.firstName?.[0] || user.name?.[0] || "";
    const last = user.lastName?.[0] || "";
    return (first + last).toUpperCase() || "U";
  };

  const getUserDisplayName = () => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.name || user.email || t("users.unknownUser");
  };

  const getUserTypeDisplay = () => {
    switch (user.userType) {
      case "centre3_employee": return t("users.userTypes.centre3Employee");
      case "contractor": return t("users.userTypes.contractor");
      case "sub_contractor": return t("users.userTypes.subContractor");
      case "client": return t("users.userTypes.client");
      default: return user.userType || t("users.unknownType");
    }
  };

  const getDepartmentName = (id: number | null) => {
    if (!id) return "—";
    const dept = departments.find((d: any) => d.id === id);
    return dept?.name || "—";
  };

  const getCompanyName = (id: number | null) => {
    if (!id) return "—";
    const company = companies.find((c: any) => c.id === id);
    return company?.name || "—";
  };

  const getSiteName = (id: number | null) => {
    if (!id) return "—";
    const site = sites.find((s: any) => s.id === id);
    return site?.name || "—";
  };

  const formatDate = (date: any) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin": return "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]";
      case "user": return "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]";
      default: return "bg-[#F5F5F5] text-[#2C2C2C] border-[#E0E0E0]";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-[#D1FAE5] text-[#059669] border-[#059669]";
      case "inactive": return "bg-[#F5F5F5] text-[#2C2C2C] border-[#E0E0E0]";
      case "suspended": return "bg-[#FFE5E5] text-[#FF6B6B] border-[#FF6B6B]";
      default: return "bg-[#F5F5F5] text-[#2C2C2C] border-[#E0E0E0]";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col" showCloseButton={false}>
        {/* Header */}
        <DialogHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-[#5B2C93]/20">
                <AvatarFallback className="bg-[#E8DCF5] text-[#5B2C93] text-xl font-medium">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div>
                <DialogTitle className="text-xl text-[#5B2C93]">{getUserDisplayName()}</DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className={getRoleColor(user.role)}>
                    {(user as any).systemRole?.name || (user.role === "admin" ? t("users.administrator") : t("users.requestor"))}
                  </Badge>
                  <Badge variant="outline" className={getStatusColor(user.status || "active")}>
                    {user.status || "active"}
                  </Badge>
                  <Badge variant="secondary">
                    {getUserTypeDisplay()}
                  </Badge>
                </div>
              </div>
            </div>
            <Button onClick={onEdit} className="gap-2 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white">
              <Edit2 className="h-4 w-4" />
              {t("users.view.editUser")}
            </Button>
          </div>
        </DialogHeader>

        <Separator />

        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="general" className="gap-2">
              <User className="h-4 w-4" />
              {t("users.view.general")}
            </TabsTrigger>
            <TabsTrigger value="organization" className="gap-2">
              <Building2 className="h-4 w-4" />
              {t("users.view.organization")}
            </TabsTrigger>
            <TabsTrigger value="access" className="gap-2">
              <Shield className="h-4 w-4" />
              {t("users.view.access")}
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 min-h-0 overflow-auto mt-4">
            {/* General Tab */}
            <TabsContent value="general" className="m-0 space-y-6">
              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.personalInformation")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.firstNameEn")} value={user.firstName} />
                  <FieldDisplay label={t("users.view.lastNameEn")} value={user.lastName} />
                  <FieldDisplay label={t("users.view.firstNameAr")} value={user.firstNameAr} />
                  <FieldDisplay label={t("users.view.lastNameAr")} value={user.lastNameAr} />
                </div>
              </div>
              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.identityInformation")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.idType")} value={
                    user.idType === "national_id" ? t("users.view.nationalId") :
                    user.idType === "iqama" ? t("users.view.iqama") : user.idType
                  } />
                  <FieldDisplay label={t("users.view.idNumber")} value={user.idNumber} />
                </div>
              </div>
              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.contactInformation")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.emailAddress")} value={user.email}
                    icon={<Mail className="h-4 w-4 text-[#6B6B6B]" />} />
                  <FieldDisplay label={t("users.view.mobileNumber")} value={user.phone}
                    icon={<Phone className="h-4 w-4 text-[#6B6B6B]" />} />
                </div>
              </div>
            </TabsContent>

            {/* Organization Tab */}
            <TabsContent value="organization" className="m-0 space-y-6">
              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.employmentInformation")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.jobTitle")} value={user.jobTitle}
                    icon={<Briefcase className="h-4 w-4 text-[#6B6B6B]" />} />
                  <FieldDisplay label={t("users.view.employeeId")} value={user.employeeId} />
                  <FieldDisplay label={t("users.view.department")} value={getDepartmentName(user.departmentId)} />
                  <FieldDisplay label={t("users.view.reportsTo")} value={user.managerName || "—"} />
                </div>
              </div>

              {(user.userType === "contractor" || user.userType === "sub_contractor") && (
                <div className="bg-white rounded-lg border p-5">
                  <SectionHeader title={t("users.view.contractorAssignment")} />
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <FieldDisplay label={t("users.view.contractorCompany")} value={getCompanyName(user.contractorCompanyId)}
                      icon={<Building2 className="h-4 w-4 text-[#6B6B6B]" />} />
                    {user.userType === "sub_contractor" && (
                      <FieldDisplay label={t("users.view.subContractorCompany")} value={getCompanyName(user.subContractorCompanyId)} />
                    )}
                    <FieldDisplay label={t("users.view.contractReference")} value={user.contractReference}
                      icon={<FileText className="h-4 w-4 text-[#6B6B6B]" />} />
                    <FieldDisplay label={t("users.view.contractExpiry")} value={formatDate(user.contractExpiry)}
                      icon={<Calendar className="h-4 w-4 text-[#6B6B6B]" />} />
                    <FieldDisplay label={t("users.view.centre3Contact")} value={user.reportingToName || "—"} />
                  </div>
                </div>
              )}

              {user.userType === "client" && (
                <div className="bg-white rounded-lg border p-5">
                  <SectionHeader title={t("users.view.clientAssignment")} />
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <FieldDisplay label={t("users.view.clientCompany")} value={getCompanyName(user.clientCompanyId)}
                      icon={<Building2 className="h-4 w-4 text-[#6B6B6B]" />} />
                    <FieldDisplay label={t("users.view.accountManager")} value={user.accountManagerName || "—"} />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Access Tab */}
            <TabsContent value="access" className="m-0 space-y-6">
              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.rolePermissions")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.systemRole")} value={
                    <Badge variant="outline" className={getRoleColor(user.role)}>
                      {(user as any).systemRole?.name || (user.role === "admin" ? t("users.administrator") : t("users.requestor"))}
                    </Badge>
                  } />
                  <FieldDisplay label={t("common.status")} value={
                    <Badge variant="outline" className={getStatusColor(user.status || "active")}>
                      {user.status || "active"}
                    </Badge>
                  } />
                </div>
              </div>

              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.siteAccess")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.siteAccessLabel")} 
                    value={user.allSitesAccess ? t("users.view.allSites") : t("users.view.specificSites")}
                    icon={<MapPin className="h-4 w-4 text-[#6B6B6B]" />} />
                  {!user.allSitesAccess && user.siteIds && (
                    <div className="col-span-2">
                      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2">{t("users.view.assignedSites")}</p>
                      <div className="flex flex-wrap gap-2">
                        {user.siteIds.map((siteId: number) => (
                          <Badge key={siteId} variant="secondary">{getSiteName(siteId)}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg border p-5">
                <SectionHeader title={t("users.view.accountInformation")} />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldDisplay label={t("users.view.created")} value={formatDate(user.createdAt)}
                    icon={<Calendar className="h-4 w-4 text-[#6B6B6B]" />} />
                  <FieldDisplay label={t("users.view.lastSignIn")} value={formatDate(user.lastSignedIn)} />
                  <FieldDisplay label={t("users.view.loginMethod")} value={user.loginMethod || t("users.view.password")} />
                  {user.userType === "contractor" && (
                    <FieldDisplay label={t("users.view.accountExpiresWithContract")}
                      value={user.accountExpiresWithContract ? t("common.yes") : t("common.no")} />
                  )}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
